import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import BiteDBAPI from '../../../api/BiteDBAPI';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  type: 'supplier' | 'product';
  selected?: { id: string; name: string };
}

const LinkProductSupplierModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  type,
  selected,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;

  const [ items, setItems ] = useState<any[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ saving, setSaving ] = useState(false);
  const [ checked, setChecked ] = useState<Set<string>>(new Set());
  const [ relatedProducts, setRelatedProducts ] = useState<any[]>([]);

  useEffect(() => {
    const fetchRelatedItems = async() => {
      if (!selected?.id) {
        return;
      }

      setLoading(true);
      try {
        let response;
        if (type === 'product') {
          response = await BiteDBAPI.getSuppliersByProductId(selected.id);
        } else {
          response = await BiteDBAPI.getProductsBySupplierId(selected.id);
        }
        if (response?.success) {
          const listOfIds = response.result.map((item: any) => item.id);
          setChecked(new Set(listOfIds));
          setRelatedProducts(response.result || []);
        }
      } catch (error) {
        console.error('Error fetching related items:', error);
      } finally {
        setLoading(false);
      }
    };

    if (relatedProducts.length === 0 && selected?.id) {
      fetchRelatedItems();
    }
  }, [ selected?.id, type, , relatedProducts.length ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let mounted = true;
    (async() => {
      try {
        setLoading(true);
        const resp =
          type === 'supplier' ?
            await BiteDBAPI.getProductsByGroupId(groupId) :
            await BiteDBAPI.getEntitiesByGroupId(groupId);

        if (mounted && resp?.success) {
          const ordered = resp.result.sort((a: any, b: any) => {
            return a.name.localeCompare(b.name);
          });
          setItems(ordered || []);
        }
      } catch (e) {
        console.error('Error fetching list:', e);
        enqueueSnackbar('Error loading records', { variant: 'error' });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
      setItems([]);
      setChecked(new Set());
    };
  }, [ isOpen, type, groupId ]);

  const dialogTitle = useMemo(
    () =>
      type === 'supplier' ?
        `Link Products to Supplier (${selected?.name})` :
        `Link Suppliers to Product (${selected?.name})`,
    [ type ],
  );

  const handleToggle = (id: string) =>
    setChecked((prev) => {
      const copy = new Set(prev);
      if (copy.has(id)) {
        copy.delete(id);
      } else {
        copy.add(id);
      }
      return copy;
    });

  const relationships = useMemo(
    () =>
      Array.from(checked).map((id) =>
        type === 'supplier' ?
          { supplier_id: selected?.id, product_id: id, price: 0 } :
          { supplier_id: id, product_id: selected?.id, price: 0 },
      ),
    [ checked, selected?.id, type ],
  );

  const handleSave = async() => {
    if (relationships.length === 0 || saving) {
      return;
    }

    try {
      setSaving(true);
      const resp = await BiteDBAPI.createBulkSupplierProducts(relationships);

      if (resp?.success) {
        enqueueSnackbar('Links created successfully', {
          variant: 'success',
        });
        onClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(`Error linking: ${resp?.result}`, {
          variant: 'error',
        });
      }
    } catch (e) {
      console.error('Linking error', e);
      enqueueSnackbar('Unexpected error while linking', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      className='dialog_box'
      open={isOpen}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--bg-colors)',
          borderRadius: '0.5rem',
          overflowY: 'visible',
          maxWidth: '640px',
          width: '90%',
          margin: '0 auto',
        },
      }}
    >
      <DialogTitle className='modal-header-content modal-title'>
        {dialogTitle}
        <div
          className='audit-add-modal-head-cont-icon'
          onClick={onClose}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
          >
            <path
              d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </DialogTitle>

      <DialogContent className='dialog-content-base' style={{ paddingTop: 0 }}>
        {loading ? (
          <div className='loading-spinner-cont-modal'>
            <CircularProgress style={{ color: '#FFF', margin: '40px 0' }} />
          </div>
        ) : (
          <div
            style={{
              maxHeight: '360px',
              overflowY: 'auto',
              paddingTop: '12px',
            }}
          >
            {items.length === 0 && (
              <p style={{ textAlign: 'center', color: 'var(--card-text)' }}>
                      No records found.
              </p>
            )}
            {items.map((item) => (
              <label
                key={item.id}
                className='link-item-row'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #2A2C32',
                }}
              >
                <Checkbox
                  checked={checked.has(item.id)}
                  onChange={() => handleToggle(item.id)}
                  sx={{
                    'color': '#4895EF',
                    '&.Mui-checked': { color: '#4895EF' },
                    'padding': '4px',
                    'marginRight': '8px',
                  }}
                />
                <span style={{ color: '#FFF' }}>{item.name}</span>
              </label>
            ))}
          </div>
        )}
      </DialogContent>

      <div className='audit-modal-cont-footer'>
        <div className='audit-modal-cont-footer-content'>
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={onClose}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Cancel
            </div>
          </div>
          <div
            className={`audit-modal-cont-footer-content-btn-upload${
              relationships.length === 0 || saving ?
                ' opacity-50 cursor-not-allowed' :
                ''
            }`}
            onClick={
              relationships.length === 0 || saving ? undefined : handleSave
            }
          >
            <div className='audit-modal-cont-footer-content-btn-text' style={{ color: '#FFF' }}>
              {saving ? 'Saving...' : 'Link'}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default LinkProductSupplierModal;
